import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TimelineCanvas = ({
  itinerary,
  onDrop,
  onDragOver,
  onRemoveActivity,
  onAddDay
}) => {
  const getCategoryIcon = (category) => {
    const icons = {
      activity: 'Waves',
      accommodation: 'Hotel',
      food: 'Utensils',
      transport: 'Car',
      wellness: 'Heart',
      sightseeing: 'Camera',
      shopping: 'ShoppingBag',
      entertainment: 'Music'
    };
    return icons?.[category] || 'MapPin';
  };

  const getCategoryColor = (category) => {
    const colors = {
      activity: 'border-teal-300 bg-teal-50',
      accommodation: 'border-purple-300 bg-purple-50',
      food: 'border-orange-300 bg-orange-50',
      transport: 'border-blue-300 bg-blue-50',
      wellness: 'border-pink-300 bg-pink-50',
      sightseeing: 'border-purple-300 bg-purple-50',
      shopping: 'border-green-300 bg-green-50',
      entertainment: 'border-yellow-300 bg-yellow-50'
    };
    return colors?.[category] || 'border-gray-300 bg-gray-50';
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmed: 'bg-green-500',
      pending: 'bg-yellow-500',
      available: 'bg-blue-500'
    };
    return colors?.[status] || colors?.available;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="bg-background">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Trip Timeline</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Drag activities from the sidebar to organize your daily schedule
            </p>
          </div>
          <Button
            variant="default"
            size="sm"
            iconName="Plus"
            onClick={onAddDay}
          >
            Add Day
          </Button>
        </div>

        {/* Timeline */}
        <div className="space-y-6">
          {itinerary?.map((day, dayIndex) => (
            <div key={day?.day} className="bg-card rounded-xl border border-border overflow-hidden shadow-sm">
              {/* Day Header */}
              <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold text-lg">
                    {day?.day}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground">{day?.title}</h3>
                    <p className="text-sm text-muted-foreground">{formatDate(day?.date)}</p>
                  </div>
                </div>
              </div>

              {/* Time Slots */}
              <div className="p-4 space-y-3">
                {day?.timeSlots?.map((slot, slotIndex) => (
                  <div
                    key={slot?.id}
                    onDrop={onDrop(dayIndex, slotIndex)}
                    onDragOver={onDragOver}
                    className={`min-h-[80px] rounded-lg border-2 border-dashed transition-all ${
                      slot?.activity
                        ? `${getCategoryColor(slot?.activity?.category)} border-solid`
                        : 'border-border bg-muted/30 hover:border-primary/50 hover:bg-primary/5'
                    }`}
                  >
                    {slot?.activity ? (
                      <div className="p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1">
                            <div className="flex flex-col items-center gap-1">
                              <div className={`w-2 h-2 rounded-full ${getStatusColor(slot?.activity?.status)}`} />
                              <div className="w-px h-full bg-border" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-1 rounded">
                                  {slot?.time}
                                </span>
                                <span className="text-xs text-muted-foreground capitalize">
                                  {slot?.activity?.category}
                                </span>
                                <div className={`w-6 h-6 rounded flex items-center justify-center ${getCategoryColor(slot?.activity?.category)}`}>
                                  <Icon name={getCategoryIcon(slot?.activity?.category)} size={12} />
                                </div>
                              </div>
                              <h4 className="font-semibold text-foreground mb-1">{slot?.activity?.title}</h4>
                              <p className="text-sm text-muted-foreground mb-2">{slot?.activity?.description}</p>
                              <div className="flex items-center gap-3 text-xs">
                                <span className="flex items-center gap-1">
                                  <Icon name="Clock" size={12} className="text-muted-foreground" />
                                  <span className="text-muted-foreground">{slot?.activity?.duration}h duration</span>
                                </span>
                                <span className="flex items-center gap-1">
                                  <Icon name="Coins" size={12} className="text-accent" />
                                  <span className="font-semibold text-accent">₱{slot?.activity?.cost?.toLocaleString()}</span>
                                </span>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => onRemoveActivity(dayIndex, slotIndex)}
                            className="w-8 h-8 rounded-lg hover:bg-destructive/10 flex items-center justify-center transition-colors group"
                            aria-label="Remove activity"
                          >
                            <Icon name="X" size={16} className="text-muted-foreground group-hover:text-destructive" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="h-full flex items-center justify-center p-4">
                        <div className="text-center">
                          <Icon name="Plus" size={20} className="mx-auto mb-1 text-muted-foreground opacity-50" />
                          <p className="text-xs text-muted-foreground">
                            {slot?.time} - Drop activity here
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Accommodation Section */}
              <div className="border-t border-border p-4 bg-secondary/5">
                {day?.accommodation ? (
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center flex-shrink-0">
                      <Icon name="Hotel" size={18} className="text-secondary" />
                    </div>
                    <div className="flex-1">
                      <span className="text-xs font-semibold text-secondary">Accommodation</span>
                      <h4 className="font-semibold text-foreground mt-1">{day?.accommodation?.name}</h4>
                      <p className="text-sm text-muted-foreground">{day?.accommodation?.address}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <Icon name="Coins" size={12} className="text-accent" />
                        <span className="text-sm font-semibold text-accent">₱{day?.accommodation?.cost?.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-3">
                    <button className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2 mx-auto">
                      <Icon name="Plus" size={14} />
                      Add accommodation for this day
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Add Day Button */}
        <button
          onClick={onAddDay}
          className="w-full py-6 rounded-xl border-2 border-dashed border-border hover:border-primary/50 hover:bg-primary/5 transition-all flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground"
        >
          <Icon name="Plus" size={20} />
          <span className="font-medium">Add Another Day</span>
        </button>
      </div>
    </div>
  );
};

export default TimelineCanvas;